export type ReliabilityAttributes = Record<string, string | number | boolean | undefined>;

export type StartOptions = {
	attributes?: ReliabilityAttributes;
	id?: string;
	name: string;
	touchpointId?: string;
};

export type SucceedOptions = {
	attributes?: ReliabilityAttributes;
	id?: string;
	name: string;
};

export type FailOptions = {
	attributes?: ReliabilityAttributes;
	error: Error;
	id?: string;
	name: string;
	traceId?: string;
};

export type AbortOptions = {
	attributes?: ReliabilityAttributes;
	id?: string;
	name: string;
	reason?: string;
};

export type ReliabilityEvent =
	| {
			attributes?: ReliabilityAttributes;
			id?: string;
			name: string;
			touchpointId?: string;
			type: 'start';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			id?: string;
			name: string;
			touchpointId?: string;
			type: 'succeed';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			error: Error;
			id?: string;
			name: string;
			touchpointId?: string;
			traceId?: string;
			type: 'fail';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			id?: string;
			name: string;
			reason?: string;
			touchpointId?: string;
			type: 'abort';
	  };

export type ReliabilitySubscriber = (event: ReliabilityEvent) => void;

type ActiveTask = {
	attributes?: ReliabilityAttributes;
	id?: string;
	name: string;
	startedAt: number;
	touchpointId?: string;
};

const buildKey = (name: string, id?: string): string =>
	id === undefined ? name : `${name}::${id}`;

export class StudioReliabilityTracker {
	private activeTasks = new Map<string, ActiveTask>();
	private subscribers = new Set<ReliabilitySubscriber>();

	subscribe(subscriber: ReliabilitySubscriber): () => void {
		this.subscribers.add(subscriber);
		return () => {
			this.subscribers.delete(subscriber);
		};
	}

	start({ name, id, touchpointId, attributes }: StartOptions): void {
		const key = buildKey(name, id);

		if (this.activeTasks.has(key)) {
			return;
		}

		this.activeTasks.set(key, {
			name,
			id,
			touchpointId,
			attributes,
			startedAt: performance.now(),
		});

		this.emit({ type: 'start', name, id, touchpointId, attributes });
	}

	succeed({ name, id, attributes }: SucceedOptions): void {
		const activeTask = this.consumeActiveTask(name, id);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'succeed',
			name,
			id,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	fail({ name, id, error, traceId, attributes }: FailOptions): void {
		const activeTask = this.consumeActiveTask(name, id);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'fail',
			name,
			id,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			error,
			traceId,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	abort({ name, id, reason, attributes }: AbortOptions): void {
		const activeTask = this.consumeActiveTask(name, id);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'abort',
			name,
			id,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			reason,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	private consumeActiveTask(name: string, id?: string): ActiveTask | undefined {
		const key = buildKey(name, id);
		const activeTask = this.activeTasks.get(key);
		if (!activeTask) {
			return undefined;
		}
		this.activeTasks.delete(key);
		return activeTask;
	}

	private emit(event: ReliabilityEvent): void {
		this.subscribers.forEach((subscriber) => {
			try {
				subscriber(event);
			} catch {
				// no-op
			}
		});
	}
}

export const studioReliabilityTracker: StudioReliabilityTracker = new StudioReliabilityTracker();
